import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { ACCOUNTS } from "../../constants";
import { createAgentPayment, updateAgentPayment } from "./api/agentsApi";

function todayStr() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

// Record or edit a commission payment we made to an agent. Pass `payment` to edit.
export default function AgentPaymentDialog({ open, onClose, agentId, payment, onSaved }) {
  const isEdit = !!payment;
  const [paymentDate, setPaymentDate] = useState(todayStr());
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentAccount, setPaymentAccount] = useState(ACCOUNTS[0]?.value || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setPaymentDate(payment.paymentDate || todayStr());
      setPaymentAmount(payment.paymentAmount != null ? String(payment.paymentAmount) : "");
      setPaymentAccount(payment.paymentAccount || ACCOUNTS[0]?.value || "");
    } else {
      setPaymentDate(todayStr());
      setPaymentAmount("");
      setPaymentAccount(ACCOUNTS[0]?.value || "");
    }
    setSaving(false);
    setError(null);
  }, [open, isEdit, payment]);

  // Keep a free-text account that isn't in the canonical list (e.g. a legacy or
  // DISCOUNT: value) selectable when editing, so it isn't silently lost.
  const accountOptions = useMemo(() => {
    if (paymentAccount && !ACCOUNTS.some((a) => a.value === paymentAccount)) {
      return [{ value: paymentAccount, label: paymentAccount }, ...ACCOUNTS];
    }
    return ACCOUNTS;
  }, [paymentAccount]);

  const canSave = useMemo(() => {
    const n = Number(paymentAmount);
    return !!paymentDate && Number.isFinite(n) && n > 0 && !!paymentAccount;
  }, [paymentDate, paymentAmount, paymentAccount]);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      const body = {
        paymentDate,
        paymentAmount: Number(paymentAmount),
        paymentAccount,
      };
      if (isEdit) {
        await updateAgentPayment(agentId, payment.paymentId, body);
      } else {
        await createAgentPayment(agentId, body);
      }
      await onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isEdit ? "Edit commission payment" : "Record commission payment"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

          <TextField
            label="Date"
            type="date"
            size="small"
            fullWidth
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Amount £"
            type="number"
            size="small"
            fullWidth
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel id="payment-account">Account</InputLabel>
            <Select
              labelId="payment-account"
              label="Account"
              value={paymentAccount}
              onChange={(e) => setPaymentAccount(e.target.value)}
            >
              {accountOptions.map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? "Saving…" : isEdit ? "Save" : "Record payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
